import { useEffect, useState } from "react";

/** phone < 768 ≤ tablet < 1024 ≤ desktop. A touch iPad stays "tablet" up to 1440 (landscape, Stage Manager):
 *  docking a tool panel there squeezed the chat into a sliver, so tools float over the chat instead. */
export const TABLET_QUERY = "(min-width: 768px)";
export const DESKTOP_QUERY = "(min-width: 1024px) and (pointer: fine), (min-width: 1440px)";

export function useMediaQuery(query: string): boolean {
  const [match, setMatch] = useState(() => typeof window !== "undefined" && window.matchMedia(query).matches);
  useEffect(() => {
    const m = window.matchMedia(query);
    const on = () => setMatch(m.matches);
    on();
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, [query]);
  return match;
}

/** "phone" | "tablet" | "desktop", following the viewport live (rotation, split view, window resize). */
export function useDevice(): "phone" | "tablet" | "desktop" {
  const tablet = useMediaQuery(TABLET_QUERY);
  const desktop = useMediaQuery(DESKTOP_QUERY);
  return desktop ? "desktop" : tablet ? "tablet" : "phone";
}

/** Horizontal swipe from a screen edge (phone navigation drawer). `side` is the physical edge. */
export function useEdgeSwipe(enabled: boolean, side: "left" | "right", onSwipe: () => void) {
  useEffect(() => {
    if (!enabled) return;
    let start: { x: number; y: number; t: number } | null = null;
    const down = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!t) return;
      const fromEdge = side === "left" ? t.clientX : window.innerWidth - t.clientX;
      start = fromEdge <= 24 ? { x: t.clientX, y: t.clientY, t: Date.now() } : null;
    };
    const up = (e: TouchEvent) => {
      const t = e.changedTouches[0];
      if (!start || !t) return;
      const dx = (t.clientX - start.x) * (side === "left" ? 1 : -1);
      const dy = Math.abs(t.clientY - start.y);
      if (dx > 60 && dy < 50 && Date.now() - start.t < 600) onSwipe();
      start = null;
    };
    window.addEventListener("touchstart", down, { passive: true });
    window.addEventListener("touchend", up, { passive: true });
    return () => { window.removeEventListener("touchstart", down); window.removeEventListener("touchend", up); };
  }, [enabled, side, onSwipe]);
}
